/**
 * @module support/infinea
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

/* tslint:disable:no-any */
/* tslint:disable:no-global-variables */
/* tslint:disable:max-file-line-count */

import { Injectable } from '@angular/core';
import { LoggerService } from '@core/logger/services/logger.service';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { DlInterface } from '@support/api/common/dl.interface';
import { ScannerScanTargetTypeEnum } from '@support/infinea/constants/infinea-constants';
import {
  CreditCardInfineaResponse,
  ScannedItems,
} from '@support/infinea/interfaces/infinea.interface';
import {
  IPC_MESSAGE,
  IPC_MESSAGE_KEY,
  SHARED_PAYMENT,
} from '@support/payment/shared-payment/constants/shared-payment.constants';
import { WindowRefService } from '@support/shared/services/window-ref.service';
import { Wove } from 'aspect.js';
import * as CircularJSON from 'circular-json';
import { Observable, Subject } from 'rxjs';

declare var unknownBarcodeFunction;
declare var skuDataFunction;
declare var imeiDataFunction;
declare var simDataFunction;
declare var upcDataFunction;
declare var HandleDLData;
declare var MagneticCardData;
declare var EMVEncryptedMagneticCardData;
declare var MagneticCardReadError;
declare var IsCardInsertedData;
declare var EncryptedMagneticCardData;
declare var defaultBarcodeFunction;
declare var smartCardRemovedEarly;
declare var PINEntryTimeOut;
declare var CardInsertTimeOut;
declare var PINEntryLimitExceeded;
declare var PINPadCancelKeyPressed;
declare var IsFallBackTriggered;

@Wove({ moduleName: SHARED_PAYMENT.INFINEA_MODULE })
@Injectable()
export class InfineaService {
  /**
   * window is used to access infinea functions which was injected in global level
   */
  public readonly window: any;
  /**
   * Used to show message on IPC
   */
  public ipcMessageContent: ObjectInterface;
  private barCodeData: Subject<string>;
  private skuData: Subject<string>;
  private imeiData: Subject<string>;
  private simData: Subject<string>;
  private upcData: Subject<string>;
  private dlData: Subject<DlInterface>;
  private isCardInserted: Subject<string>;
  private isBarCode: boolean;

  /**
   * Scanner state, to be used to identify in case
   * barcode is flagged as unknown by infinea library
   */
  private scannerState: ScannerScanTargetTypeEnum;

  constructor(
    private windowRefService: WindowRefService,
    private logger: LoggerService
  ) {
    this.window = this.windowRefService.nativeWindow();
    this.barCodeData = new Subject<string>();
    this.skuData = new Subject<string>();
    this.imeiData = new Subject<string>();
    this.simData = new Subject<string>();
    this.upcData = new Subject<string>();
    this.dlData = new Subject<DlInterface>();
    this.isCardInserted = new Subject<string>();
    this.isBarCode = false;
    this.window.PINPadCancelKeyPressed = () => {
      this.pinPadCancelKeyPressed();
    };
    this.window.unknownBarcodeFunction = (barCodeData: string) => {
      if (this.isBarCode) {
        defaultBarcodeFunction(barCodeData);
      } else {
        this.unknownBarcodeFunction(barCodeData);
      }
    };
    this.window.skuDataFunction = (skuData: string) => {
      this.skuDataFunction(skuData);
    };
    this.window.imeiDataFunction = (imeiData: string) => {
      this.imeiDataFunction(imeiData);
    };
    this.window.simDataFunction = (simData: string) => {
      this.simDataFunction(simData);
    };
    this.window.upcDataFunction = (upcData: string) => {
      this.upcDataFunction(upcData);
    };
    this.window.HandleDLData = (dlData: DlInterface) => {
      this.HandleDLData(dlData);
    };
  }

  /**
   * Provide barcode data
   * @returns {Observable<string>}
   */
  public getDefaultBarcode(): Observable<string> {
    this.isBarCode = true;
    const barcodeData = new Subject<string>();
    this.window.defaultBarcodeFunction = (barcode: string) => {
      this.isBarCode = false;
      barcodeData.next(barcode);
      this.logData({
        request: { method: 'getDefaultBarcode' },
        response: { method: 'defaultBarcodeFunction', data: barcodeData },
      });
      barcodeData.complete();
    };
    this.scannerState = undefined;
    this.logData({
      request: { method: 'getDefaultBarcode' },
      response: { method: 'defaultBarcodeFunction', data: barcodeData },
    });
    return barcodeData.asObservable();
  }

  /**
   * Provides card insert status
   * @return {boolean} Observable<boolean>
   */
  public getCardInsertStatus(): Observable<string> {
    if (this.isCardInserted && !this.isCardInserted.closed) {
      this.logData({
        request: { method: 'getCardInsertStatus' },
        response: { method: 'getCardInsertStatus', data: this.isCardInserted },
      });
      this.isCardInserted.complete();
    }
    this.isCardInserted = new Subject<string>();
    this.logData({
      request: { method: 'getCardInsertStatus' },
      response: { method: 'getCardInsertStatus', data: this.isCardInserted },
    });
    return this.isCardInserted.asObservable();
  }

  /**
   * Provides card details
   * @return {cardData} Observable<CreditCardInfineaResponse>
   */
  public getInfineaCardData(
    amount?: string,
    isEmvEnabled?: boolean,
    isManualImprint?: boolean
  ): Observable<CreditCardInfineaResponse> {
    const loggerReq = {
      method: 'getInfineaCardData',
      amount: amount,
      isEmvEnabled: isEmvEnabled,
      isManualImprint: isManualImprint,
    };
    this.logData({ request: loggerReq, response: { method: 'getInfineaCardData' } });
    const FIXED_TWO_DECIMAL = 2;
    let cardDataReceived = false;
    let isCardError = false;
    let isCardInserted = false;
    amount = amount ? amount.replace(/\`|\$|,|\s/g, '') : '0';
    amount = Number(amount).toFixed(FIXED_TWO_DECIMAL);
    if (!isManualImprint) {
      this.window.Infinea.NFC.powerdown();
      this.window.Infinea.NFC.powerup(amount);
    }
    const displayAmount =
      'Amount: ' + (Number(amount) >= 0 ? '$' : '-$') + Math.abs(Number(amount)).toString();
    if (isManualImprint) {
      this.window.Infinea.EMV.manualEntryPrompt(
        IPC_MESSAGE_KEY.authorizing,
        IPC_MESSAGE_KEY.amount + amount + '\n' + IPC_MESSAGE_KEY.swipeOrTapCard,
        this.window.Infinea.Fonts.FONT_6X8
      );
    } else if (isEmvEnabled) {
      this.displayIpcMessage(
        displayAmount + '\n' + this.getMessage(IPC_MESSAGE_KEY.pleaseInsertSwipeTapToPay)
      );
      this.window.smartCardRemovedEarly = () => {
        cardDataReceived = true;
        this.displayIpcMessage(
          displayAmount + '\n' + this.getMessage(IPC_MESSAGE_KEY.pleaseInsertSwipeTapToPay)
        );
        this.isCardInserted.next(SHARED_PAYMENT.CARD_REMOVED_EARLY);
        this.logData({
          request: loggerReq,
          response: { method: 'smartCardRemovedEarly', data: this.isCardInserted },
        });
      };
      this.window.IsFallBackTriggered = () => {
        this.isCardInserted.next(SHARED_PAYMENT.TECHNICAL_FALLBACK);
        this.logData({
          request: loggerReq,
          response: { method: 'IsFallBackTriggered', data: this.isCardInserted },
        });
      };
      let previousValue = isCardInserted;
      this.window.IsCardInsertedData = (isInserted: string) => {
        isCardInserted = isInserted === 'true';
        if (previousValue !== isCardInserted) {
          previousValue = isCardInserted;
          if ((!isCardError && isCardInserted) || !isCardInserted) {
            this.isCardInserted.next(
              isCardInserted ? SHARED_PAYMENT.CARD_INSERTED : SHARED_PAYMENT.CARD_REMOVED
            );
            this.logData({
              request: loggerReq,
              response: { method: 'IsCardInsertedData', data: this.isCardInserted },
            });
          } else if (!cardDataReceived) {
            this.isCardInserted.next(SHARED_PAYMENT.CARD_REMOVED_EARLY);
            this.logData({
              request: loggerReq,
              response: { method: 'IsCardInsertedData', data: this.isCardInserted },
            });
          }
        }
      };
      this.window.PINEntryTimeOut = () => {
        cardDataReceived = true;
        if (isCardInserted) {
          this.window.Infinea.EMV.postRemoveCardIndicator(
            this.getMessage(IPC_MESSAGE_KEY.timeOutRemoveCard),
            this.window.Infinea.Fonts.FONT_6X8
          );
        }
        this.isCardInserted.next(SHARED_PAYMENT.PIN_ENTRY_TIME_OUT);
        this.logData({
          request: loggerReq,
          response: { method: 'PINEntryTimeOut', data: this.isCardInserted },
        });
        cardData.complete();
      };
      this.window.CardInsertTimeOut = () => {
        cardDataReceived = true;
        this.displayIpcMessage(this.getMessage(IPC_MESSAGE_KEY.timeOutTransactionCancelled));
        this.isCardInserted.next(SHARED_PAYMENT.CARD_INSERT_TIME_OUT);
        this.logData({
          request: loggerReq,
          response: { method: 'CardInsertTimeOut', data: this.isCardInserted },
        });
        cardData.complete();
      };
      this.window.PINEntryLimitExceeded = () => {
        cardDataReceived = true;
        this.postCancelledMessage(isCardInserted);
        this.isCardInserted.next(SHARED_PAYMENT.PIN_ENTRY_LIMIT_EXCEEDED);
        this.logData({
          request: loggerReq,
          response: { method: 'PINEntryLimitExceeded', data: this.isCardInserted },
        });
        cardData.complete();
      };
    } else {
      this.displayIpcMessage(
        displayAmount + '\n' + this.getMessage(IPC_MESSAGE_KEY.pleaseSwipeTapToPay)
      );
    }
    const cardData = new Subject<CreditCardInfineaResponse>();
    this.window.EncryptedMagneticCardData = (card: CreditCardInfineaResponse) => {
      this.logData({
        request: loggerReq,
        response: { method: 'EncryptedMagneticCardData', data: card },
      });
      cardDataReceived = true;
      this.displayIpcMessage(this.getMessage(IPC_MESSAGE_KEY.processingPleaseWait));
      card.isEncryptionOn = true;
      card.isCardInserted = isCardInserted;
      cardData.next(card);
      cardData.complete();
    };
    this.window.EMVEncryptedMagneticCardData = (card: CreditCardInfineaResponse) => {
      this.logData({
        request: loggerReq,
        response: { method: 'EMVEncryptedMagneticCardData', data: card },
      });
      cardDataReceived = true;
      if (isCardInserted) {
        this.window.Infinea.EMV.postRemoveCardIndicator(
          this.getMessage(IPC_MESSAGE_KEY.pleaseRemoveCard),
          this.window.Infinea.Fonts.FONT_6X8
        );
      } else {
        this.displayIpcMessage(this.getMessage(IPC_MESSAGE_KEY.processingPleaseWait));
      }
      card.isEncryptionOn = true;
      card.isCardInserted = isCardInserted;
      cardData.next(card);
      cardData.complete();
    };
    this.window.MagneticCardData = (card: CreditCardInfineaResponse) => {
      this.logData({ request: loggerReq, response: { method: 'MagneticCardData', data: card } });
      cardDataReceived = true;
      this.displayIpcMessage(this.getMessage(IPC_MESSAGE_KEY.processingPleaseWait));
      card.isEncryptionOn = false;
      card.isCardInserted = isCardInserted;
      cardData.next(card);
      cardData.complete();
    };
    this.window.MagneticCardReadError = (errorMessage: string) => {
      this.logData({
        request: loggerReq,
        response: { method: 'MagneticCardReadError', data: errorMessage },
      });
      cardDataReceived = true;
      isCardError = true;
      this.postCancelledMessage(isCardInserted, errorMessage);
      cardData.error(errorMessage);
      cardData.complete();
    };
    return cardData.asObservable();
  }
  public displayIpcMessage(message: string): void {
    this.logData({
      request: { method: 'displayIpcMessage', message: message },
      response: { method: 'postEMVMessage' },
    });
    this.window.Infinea.EMV.postEMVMessage(message, this.window.Infinea.Fonts.FONT_6X8);
  }
  public resetIpc(): void {
    this.logData({ request: { method: 'resetIpc' }, response: { method: 'powerdown' } });
    this.window.Infinea.NFC.powerdown();
  }
  public getBarCodeData(): Observable<any> {
    this.scannerState = undefined;
    this.logData({
      request: { method: 'getBarCodeData' },
      response: { method: 'barCodeData', data: this.barCodeData.asObservable() },
    });
    return this.barCodeData.asObservable();
  }

  public getImeiData(): Observable<any> {
    this.imeiData = new Subject<string>();
    this.scannerState = ScannerScanTargetTypeEnum.IMEI;
    this.logData({
      request: { method: 'getImeiData' },
      response: { method: 'imeiData', data: this.imeiData.asObservable() },
    });
    return this.imeiData.asObservable();
  }
  public getSkuData(): Observable<any> {
    this.skuData = new Subject<string>();
    this.scannerState = ScannerScanTargetTypeEnum.SKU;
    this.logData({
      request: { method: 'getSkuData' },
      response: { method: 'skuData', data: this.skuData.asObservable() },
    });
    return this.skuData.asObservable();
  }
  public getSimData(): Observable<any> {
    this.simData = new Subject<string>();
    this.scannerState = ScannerScanTargetTypeEnum.SIM;
    this.logData({
      request: { method: 'getSimData' },
      response: { method: 'simData', data: this.simData.asObservable() },
    });
    return this.simData.asObservable();
  }
  public getUpcData(): Observable<any> {
    this.upcData = new Subject<string>();
    this.scannerState = ScannerScanTargetTypeEnum.UPC;
    this.logData({
      request: { method: 'getUpcData' },
      response: { method: 'upcData', data: this.dlData.asObservable() },
    });
    return this.upcData.asObservable();
  }
  public getDlData(): Observable<any> {
    this.scannerState = ScannerScanTargetTypeEnum.DL;
    this.logData({
      request: { method: 'getDlData' },
      response: { method: 'dlData', data: this.dlData.asObservable() },
    });
    return this.dlData.asObservable();
  }
  /**
   * Capture pin from Infinea
   * @param {string} amount
   * @returns {Observable<string>}
   */
  public getPin(amount?: string, invalidPin?: boolean): Observable<string> {
    const loggerReq = {
      method: 'getPin',
      amount: amount,
      invalidPin: invalidPin,
    };
    if (invalidPin) {
      this.window.Infinea.EMV.pinEntryPrompt(this.getMessage(IPC_MESSAGE_KEY.reEntryPin) + amount);
      this.logData({ request: loggerReq, response: { method: 'pinEntryPrompt' } });
    } else {
      this.window.Infinea.EMV.pinEntryPrompt(this.getMessage(IPC_MESSAGE_KEY.entryPin) + amount);
      this.logData({ request: loggerReq, response: { method: 'pinEntryPrompt' } });
    }
    const debitCardPin = new Subject<string>();
    this.window.PinPadData = (pin: string) => {
      this.logData({ request: loggerReq, response: { method: 'PinPadData', data: pin } });
      debitCardPin.next(pin);
      debitCardPin.complete();
    };
    this.window.PINPadCancelKeyPressed = () => {
      this.logData({
        request: loggerReq,
        response: { method: 'PINPadCancelKeyPressed', data: 'cancel' },
      });
      debitCardPin.error('cancel');
      debitCardPin.complete();
    };
    this.window.PinPadError = () => {
      this.logData({ request: loggerReq, response: { method: 'PinPadError', data: 'error' } });
      debitCardPin.error('error');
      debitCardPin.complete();
    };
    this.window.PINEntryTimeOut = () => {
      this.logData({
        request: loggerReq,
        response: { method: 'PINEntryTimeOut', data: 'Time Out' },
      });
      this.isCardInserted.next(SHARED_PAYMENT.PIN_ENTRY_TIME_OUT);
      debitCardPin.complete();
    };
    this.window.PINEntryLimitExceeded = () => {
      this.logData({
        request: loggerReq,
        response: { method: 'PINEntryLimitExceeded', data: 'pin entry limit exceeded' },
      });
      this.isCardInserted.next(SHARED_PAYMENT.PIN_ENTRY_LIMIT_EXCEEDED);
      debitCardPin.complete();
    };
    this.logData({
      request: loggerReq,
      response: { method: 'getPin', data: debitCardPin.asObservable() },
    });
    return debitCardPin.asObservable();
  }

  /**
   * Used to show verify amount and proceed further to debit or credit(in hybrid scenario)
   * @param amount
   * @param isHybrid
   */
  public triggerVerifyMessage(amount?: string, isHybrid?: boolean): Observable<string> {
    let message =
      this.getMessage(IPC_MESSAGE_KEY.authorize) +
      amount +
      this.getMessage(IPC_MESSAGE_KEY.debitPrompt);
    if (isHybrid) {
      message += this.getMessage(IPC_MESSAGE_KEY.creditPrompt);
    } else {
      message += this.getMessage(IPC_MESSAGE_KEY.cancelPrompt);
    }
    this.logData({
      request: { method: 'triggerVerifyMessage' },
      response: { method: 'displayIpcMessage', data: message },
    });
    this.displayIpcMessage(message);
    const verify = new Subject<string>();
    this.window.PINPadOKKeyPressed = () => {
      this.logData({
        request: { method: 'triggerVerifyMessage' },
        response: { method: 'PINPadOKKeyPressed', data: 'ok' },
      });
      verify.next('ok');
      verify.complete();
    };
    this.window.PINPadCancelKeyPressed = () => {
      this.logData({
        request: { method: 'triggerVerifyMessage' },
        response: { method: 'PINPadOKKeyPressed', data: 'error' },
      });
      verify.error('error');
      verify.complete();
    };
    return verify.asObservable();
  }
  /**
   * Overrided function for the global unknownBarcodeFunction
   * @param {string} barcode
   */
  public unknownBarcodeFunction(scannerData: any): void {
    switch (this.scannerState) {
      case ScannerScanTargetTypeEnum.SKU:
        this.skuDataFunction(scannerData);
        this.logData({
          request: { method: unknownBarcodeFunction, requestData: scannerData },
          response: { method: 'skuDataFunction' },
        });
        break;
      case ScannerScanTargetTypeEnum.DL:
        this.HandleDLData(scannerData);
        this.logData({
          request: { method: unknownBarcodeFunction, requestData: scannerData },
          response: { method: 'HandleDLData' },
        });
        break;
      case ScannerScanTargetTypeEnum.UPC:
        this.upcDataFunction(scannerData);
        this.logData({
          request: { method: unknownBarcodeFunction, requestData: scannerData },
          response: { method: 'upcDataFunction' },
        });
        break;
      case ScannerScanTargetTypeEnum.SIM:
        this.simDataFunction(scannerData);
        this.logData({
          request: { method: unknownBarcodeFunction, requestData: scannerData },
          response: { method: 'simDataFunction' },
        });
        break;
      case ScannerScanTargetTypeEnum.IMEI:
        this.imeiDataFunction(scannerData);
        this.logData({
          request: { method: unknownBarcodeFunction, requestData: scannerData },
          response: { method: 'imeiDataFunction' },
        });
        break;
      default:
        this.barCodeData.next(scannerData);
        this.barCodeData.complete();
        this.logData({
          request: { method: unknownBarcodeFunction, requestData: scannerData },
          response: { method: 'unknownBarcodeFunction', data: this.barCodeData },
        });
        break;
    }
  }
  // tslint:disable
  /**
   * Provides flag to determine the status of scanner
   * @return {boolean}
   */
  public getScanditStatus(): boolean {
    this.logData({
      request: { method: 'getScanditStatus' },
      response: {
        method: 'getCameraScannerStatus',
        data: this.window.Infinea.getCameraScannerStatus(),
      },
    });
    return this.window.Infinea.getCameraScannerStatus();
  }

  /**
   * Method to invoke scanner functionality
   * @return {void}
   */
  public startScanditScanner(type: string[] = []): Observable<ScannedItems> {
    const scanditResponse = new Subject();
    const transformScanType = type.map((type: string) => {
      return type.toUpperCase();
    });
    let request = '';
    if (type.length > 0) {
      request = JSON.stringify({
        items: [
          {
            value: transformScanType,
          },
        ],
      });
    }
    this.window.cameraScanner.startBarcodeScanning(request);
    this.window.barcodeScanResult = (response: any) => {
      const transformResponse = JSON.parse(response);
      this.logData({
        request: { method: 'startScanditScanner', requestData: request },
        response: { method: 'startBarcodeScanning', data: response },
      });
      let items = transformResponse.items;
      for (let item of items) {
        for (let key in item) {
          item[key.toLowerCase()] = item[key];
          delete item[key];
        }
      }
      this.logData({
        request: { method: 'startScanditScanner', requestData: request },
        response: { method: 'startBarcodeScanning', data: transformResponse },
      });
      scanditResponse.next(transformResponse);
      scanditResponse.complete();
    };
    return scanditResponse.asObservable();
  }
  /**
   * Overrided function for the global simDataFunction
   * @param {string} sim
   */
  private simDataFunction(simData: string): void {
    this.logData({
      request: { method: 'simDataFunction', simData: simData },
      response: { method: 'simData' },
    });
    this.simData.next(simData);
    this.simData.complete();
  }
  /**
   * Overrided function for the global skuDataFunction
   * @param {string} sku
   */
  private skuDataFunction(skuData: string): void {
    this.skuData.next(skuData);
    this.skuData.complete();
  }
  /**
   * Overrided function for the global imeiDataFunction
   * @param {string} imei
   */
  private imeiDataFunction(imeiData: string): void {
    this.imeiData.next(imeiData);
    this.imeiData.complete();
  }
  /**
   * Overrided function for the global imeiDataFunction
   * @param {string} imei
   */
  private upcDataFunction(upc: string): void {
    this.upcData.next(upc);
    this.upcData.complete();
  }
  /**
   * Overrided function for the global HandleDLData
   * @param {DlInterface} dl
   */
  private HandleDLData(dl: DlInterface): void {
    this.dlData.next(dl);
  }
  /**
   * Used to post cancelled transaction message to IPC
   * @param isCardInserted
   */
  private postCancelledMessage(isCardInserted: boolean, errorMessage?: string): void {
    if (isCardInserted) {
      if (errorMessage === SHARED_PAYMENT.BLOCKED_CARD_BLOCKED_AID) {
        this.logData({
          request: { method: 'postCancelledMessage' },
          response: {
            method: 'postRemoveCardIndicator',
            data: this.getMessage(IPC_MESSAGE_KEY.cardDeclinedRemoveCard),
          },
        });
        this.window.Infinea.EMV.postRemoveCardIndicator(
          this.getMessage(IPC_MESSAGE_KEY.cardDeclinedRemoveCard),
          this.window.Infinea.Fonts.FONT_6X8
        );
      } else if (errorMessage === SHARED_PAYMENT.PIN_ENTRY_TIME_OUT) {
        this.logData({
          request: { method: 'postCancelledMessage' },
          response: {
            method: 'postRemoveCardIndicator',
            data: this.getMessage(IPC_MESSAGE_KEY.timeOutRemoveCard),
          },
        });
        this.window.Infinea.EMV.postRemoveCardIndicator(
          this.getMessage(IPC_MESSAGE_KEY.timeOutRemoveCard),
          this.window.Infinea.Fonts.FONT_6X8
        );
      } else {
        this.logData({
          request: { method: 'postCancelledMessage' },
          response: {
            method: 'postRemoveCardIndicator',
            data: this.getMessage(IPC_MESSAGE_KEY.transactionCancelledRemoveCard),
          },
        });
        this.window.Infinea.EMV.postRemoveCardIndicator(
          this.getMessage(IPC_MESSAGE_KEY.transactionCancelledRemoveCard),
          this.window.Infinea.Fonts.FONT_6X8
        );
      }
    } else {
      if (errorMessage === SHARED_PAYMENT.BLOCKED_CARD_BLOCKED_AID) {
        this.logData({
          request: { method: 'postCancelledMessage' },
          response: {
            method: 'displayIpcMessage',
            data: this.getMessage(IPC_MESSAGE_KEY.cardDeclined),
          },
        });
        this.displayIpcMessage(this.getMessage(IPC_MESSAGE_KEY.cardDeclined));
      } else if (errorMessage === SHARED_PAYMENT.PIN_ENTRY_TIME_OUT) {
        this.logData({
          request: { method: 'postCancelledMessage' },
          response: {
            method: 'displayIpcMessage',
            data: this.getMessage(IPC_MESSAGE_KEY.timeOutTransactionCancelled),
          },
        });
        this.displayIpcMessage(this.getMessage(IPC_MESSAGE_KEY.timeOutTransactionCancelled));
      } else {
        this.logData({
          request: { method: 'postCancelledMessage' },
          response: {
            method: 'displayIpcMessage',
            data: this.getMessage(IPC_MESSAGE_KEY.transactionCancelled),
          },
        });
        this.displayIpcMessage(this.getMessage(IPC_MESSAGE_KEY.transactionCancelled));
      }
    }
  }

  private getMessage(key: string): string {
    return this.ipcMessageContent && this.ipcMessageContent[key]
      ? this.ipcMessageContent[key]
      : IPC_MESSAGE[key];
  }

  private pinPadCancelKeyPressed(): void {}

  private logData(data: object): void {
    this.logger.debug('InfineaService', 'abcd', CircularJSON.stringify(data));
  }
}
